import Immutable from 'immutable';
import { createSelector } from 'reselect';
import { parse } from 'query-string';
import { currentUserIdSelector } from '../Auth/selectors';
import { loggedInUserSelector } from '../LoggedInUser/selectors';
import { selectCurrentProfileUserProfileName } from './CurrentProfileUser/selectors';

export const usersSelector = (state) => state.Users;

export const currentStudentSelector = createSelector(
  usersSelector,
  (users) => {
    const studentProfileName = parse(window.location.search).student;
    return users.find((user) => user.get('profileName') === studentProfileName);
  }
);

export const currentUserSelector = createSelector(
  currentUserIdSelector,
  usersSelector,
  loggedInUserSelector,
  (currentUserId, users, extraUserData) => {
    let currentUser = users.get(currentUserId, null);
    if (currentUser) {
      currentUser = currentUser.merge(extraUserData);
    }
    return currentUser;
  }
);

export const selectCurrentUserLoaded = createSelector(
  currentUserIdSelector,
  currentUserSelector,
  (currentUserId, currentUser) => {
    if (currentUserId) {
      return currentUser !== null;
    }
    return true; // don't need to wait for the user summary to load for an anonymous user
  }
);

export const selectCurrentUserProfileName = createSelector(
  currentUserSelector,
  (currentUser) => {
    return currentUser ? currentUser.get('profileName', '') : '';
  }
);

export const currentUserIsEducator = createSelector(
  currentUserSelector,
  (currentUser) =>
    Boolean(currentUser && currentUser.get('isEducatorAccount', false))
);

export const selectCurrentUserEducatorStatus = createSelector(
  currentUserSelector,
  (currentUser) =>
    currentUser ? currentUser.get('educatorStatus', null) : null
);

// Select the user who's profile page or subpages are currently being viewed
export const selectCurrentlyViewProfile = createSelector(
  usersSelector,
  selectCurrentProfileUserProfileName,
  (users, profileName) => {
    return users.find((user) => user.get('profileName') === profileName);
  }
);

export const selectUsersById = (state) => state.Users;

export const usersByProfileNameSelector = createSelector(
  usersSelector,
  (users) => {
    return users.reduce(
      (current, value) => current.set(value.get('profileName'), value),
      Immutable.Map()
    );
  }
);

export const currentUserProfileNameSelector = createSelector(
  currentUserSelector,
  (currentUser) => {
    return currentUser.get('profileName');
  }
);

export const selectCurrentUserIsSuperUser = createSelector(
  currentUserSelector,
  (user) => (user ? user.get('isSuperUser', false) : false)
);

export const selectCurrentUserIsStaff = createSelector(
  currentUserSelector,
  (user) => (user ? user.get('isStaff', false) : false)
);
